import { supabase } from '../../config/supabase';

/**
 * Service for handling file uploads to Supabase Storage
 */
class StorageService {
  private readonly RECEIPTS_BUCKET = 'receipts';
  private readonly LOGOS_BUCKET = 'logos';

  /**
   * Upload a receipt file to Supabase Storage
   * @param file - The file to upload
   * @param userId - The user ID (from Firebase)
   * @param expenseId - Optional expense ID for organizing files
   * @returns The public URL of the uploaded file
   */
  async uploadReceipt(
    file: File,
    userId: string,
    expenseId?: string
  ): Promise<string> {
    try {
      // Generate a unique file name. Date.now() alone is not unique enough:
      // files in a concurrent batch can start in the same millisecond, collide
      // on the same path, and silently overwrite each other.
      const timestamp = Date.now();
      const unique = `${timestamp}-${crypto.randomUUID().slice(0, 8)}`;
      const fileExt = file.name.split('.').pop();
      const fileName = expenseId
        ? `${userId}/${expenseId}/${unique}.${fileExt}`
        : `${userId}/${unique}.${fileExt}`;

      // Upload the file
      const { data, error } = await supabase.storage
        .from(this.RECEIPTS_BUCKET)
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false,
        });

      if (error) {
        console.error('Error uploading file:', error);
        throw new Error(`Failed to upload file: ${error.message}`);
      }

      // Get the public URL
      const { data: urlData } = supabase.storage
        .from(this.RECEIPTS_BUCKET)
        .getPublicUrl(data.path);

      return urlData.publicUrl;
    } catch (error) {
      console.error('Error in uploadReceipt:', error);
      throw error;
    }
  }

  /**
   * Delete a receipt file from Supabase Storage
   * @param fileUrl - The public URL of the file to delete
   */
  async deleteReceipt(fileUrl: string): Promise<void> {
    try {
      // Extract the file path from the URL
      const url = new URL(fileUrl);
      const pathParts = url.pathname.split('/');
      const filePath = pathParts.slice(pathParts.indexOf(this.RECEIPTS_BUCKET) + 1).join('/');

      const { error } = await supabase.storage
        .from(this.RECEIPTS_BUCKET)
        .remove([filePath]);

      if (error) {
        console.error('Error deleting file:', error);
        throw new Error(`Failed to delete file: ${error.message}`);
      }
    } catch (error) {
      console.error('Error in deleteReceipt:', error);
      throw error;
    }
  }

  /**
   * List all receipts for a user
   * @param userId - The user ID (from Firebase)
   */
  async listUserReceipts(userId: string): Promise<string[]> {
    try {
      const { data, error } = await supabase.storage
        .from(this.RECEIPTS_BUCKET)
        .list(userId);

      if (error) {
        console.error('Error listing files:', error);
        throw new Error(`Failed to list files: ${error.message}`);
      }

      return data.map(file => {
        const { data: urlData } = supabase.storage
          .from(this.RECEIPTS_BUCKET)
          .getPublicUrl(`${userId}/${file.name}`);
        return urlData.publicUrl;
      });
    } catch (error) {
      console.error('Error in listUserReceipts:', error);
      throw error;
    }
  }

  /**
   * Upload a business logo to Supabase Storage
   * @param file - The logo file to upload
   * @param userId - The user ID (from Firebase)
   * @param businessId - Optional business ID for organizing files
   * @returns The public URL of the uploaded logo
   */
  async uploadLogo(
    file: File,
    userId: string,
    businessId?: string
  ): Promise<string> {
    try {
      // Validate file type (images only)
      const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp', 'image/svg+xml'];
      if (!validTypes.includes(file.type)) {
        throw new Error('Invalid file type. Please upload an image file (PNG, JPG, GIF, WEBP, SVG)');
      }

      // Validate file size (max 2MB for logos)
      const maxSize = 2 * 1024 * 1024; // 2MB
      if (file.size > maxSize) {
        throw new Error('File size too large. Maximum size is 2MB');
      }

      // Generate a unique file name (timestamp + random suffix, see uploadReceipt)
      const timestamp = Date.now();
      const unique = `${timestamp}-${crypto.randomUUID().slice(0, 8)}`;
      const fileExt = file.name.split('.').pop();
      const fileName = businessId
        ? `${userId}/${businessId}/${unique}.${fileExt}`
        : `${userId}/${unique}.${fileExt}`;

      // Upload the file
      const { data, error } = await supabase.storage
        .from(this.LOGOS_BUCKET)
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false,
        });

      if (error) {
        console.error('Error uploading logo:', error);
        throw new Error(`Failed to upload logo: ${error.message}`);
      }

      // Get the public URL
      const { data: urlData } = supabase.storage
        .from(this.LOGOS_BUCKET)
        .getPublicUrl(data.path);

      return urlData.publicUrl;
    } catch (error) {
      console.error('Error in uploadLogo:', error);
      throw error;
    }
  }

  /**
   * Delete a logo file from Supabase Storage
   * @param fileUrl - The public URL of the logo to delete
   */
  async deleteLogo(fileUrl: string): Promise<void> {
    try {
      // Extract the file path from the URL
      const url = new URL(fileUrl);
      const pathParts = url.pathname.split('/');
      const filePath = pathParts.slice(pathParts.indexOf(this.LOGOS_BUCKET) + 1).join('/');

      const { error } = await supabase.storage
        .from(this.LOGOS_BUCKET)
        .remove([filePath]);

      if (error) {
        console.error('Error deleting logo:', error);
        throw new Error(`Failed to delete logo: ${error.message}`);
      }
    } catch (error) {
      console.error('Error in deleteLogo:', error);
      throw error;
    }
  }
}

export const storageService = new StorageService();
